import React from "react";
import styled from "styled-components";
import { YStack } from "tamagui";
import { useNavigate } from "react-router-dom";
import { GBModal, GBButton, GBText, GBBadge } from "@greenbackapp/ui";
import { useAuth } from "../../contexts/AuthContext";
import { BUSINESS_BILLING_PATH, BUSINESS_START_PATH, PRACTICE_SETUP_PATH } from "../auth/paywallScope";
import { useBusinessOnSale } from "../../hooks/usePublicPricing";
import { isDemo } from "../../config/demo";

interface PaywallModalProps {
  /**
   * The user has a practice. They only meet this paywall on a personal
   * screen, so it offers the way back to the practice instead of the way
   * into one.
   */
  belongsToFirm?: boolean;
  /**
   * The user belongs to a business somebody pays for. Same reasoning: the
   * books are theirs to use without Pro, so it offers the way back to them.
   */
  inPaidBusiness?: boolean;
  /**
   * A business this user owns that nobody pays for (Dave's decision 11,
   * 2026-10-09). Its billing card is open to them without Pro, so the paywall
   * offers the way to pay for the business rather than only the personal
   * product.
   */
  unpaidOwnedBusinessId?: string | null;
}

export const PaywallModal: React.FC<PaywallModalProps> = ({
  belongsToFirm = false,
  inPaidBusiness = false,
  unpaidOwnedBusinessId = null,
}) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  // Gap G46: an owner whose business nobody pays for, with no practice and no
  // paying business to go back to. Their problem is the business bill, so the
  // paywall says so and leads with paying it (Pro comes with Business, PRD 114
  // decision 8). Everyone else keeps the Pro copy.
  const ownerToPay = !belongsToFirm && !inPaidBusiness && Boolean(unpaidOwnedBusinessId);
  const payForBusiness = () =>
    navigate(`${BUSINESS_BILLING_PATH}?business=${encodeURIComponent(unpaidOwnedBusinessId ?? "")}`);
  // PRD 86 slice 5 (design D18): somebody with no practice and no paying
  // business may have come for a business, not for Pro. Offered only once the
  // price list confirms Greenback Business is on sale: loading, failed and
  // switched off all keep today's paywall (it fails closed). Never to an owner
  // with a business to pay for, who is shown that bill instead.
  const businessOnSale = useBusinessOnSale();
  // The Live sample sells nothing (PRD 117): only the way back into the books.
  const demo = isDemo();
  const offersBusiness = !demo && businessOnSale && !belongsToFirm && !inPaidBusiness && !ownerToPay;

  return (
    <GBModal
      visible
      onDismiss={() => {}}
      title=""
      footer={
        <YStack flex={1} gap="$3">
          {ownerToPay && !demo && (
            <GBButton variant="primary" size="lg" onPress={payForBusiness}>
              Pay for your business
            </GBButton>
          )}
          {!demo && (
            <GBButton
              variant={ownerToPay ? "secondary" : "primary"}
              size={ownerToPay ? "md" : "lg"}
              onPress={() => navigate("/subscribe")}
            >
              Get Greenback Pro
            </GBButton>
          )}
          {offersBusiness && (
            <GBButton variant="secondary" size="md" onPress={() => navigate(BUSINESS_START_PATH)}>
              Set up Greenback Business
            </GBButton>
          )}
          {/* Greenback Pro is the personal product; the practice side is free
              (see paywallScope). Without this, the one screen every new
              accountant lands on offered them nothing but a purchase. */}
          {belongsToFirm ? (
            // The worklist, not /firm: the practice dashboard needs the
            // practice-workflow flag, and the worklist is HQ either way.
            <GBButton variant="secondary" size="md" onPress={() => navigate("/firm/clients")}>
              Back to Accountant HQ
            </GBButton>
          ) : inPaidBusiness ? (
            <GBButton variant="secondary" size="md" onPress={() => navigate("/business")}>
              Go to your business
            </GBButton>
          ) : ownerToPay || demo ? null : (
            <GBButton variant="secondary" size="md" onPress={() => navigate(PRACTICE_SETUP_PATH)}>
              Set up an accounting practice, free
            </GBButton>
          )}
          <GBButton
            variant="ghost"
            size="md"
            onPress={async () => { await logout(); navigate("/login", { replace: true }); }}
          >
            Sign Out
          </GBButton>
        </YStack>
      }
    >
      {ownerToPay ? (
        <YStack alignItems="center" gap="$4" paddingVertical="$2">
          <GBBadge variant="brand" size="md">
            <GBBadge.Text variant="brand" size="md">GREENBACK BUSINESS</GBBadge.Text>
          </GBBadge>
          <GBText variant="headlineMedium" bold center color="primary">
            Your business needs paying for
          </GBText>
          <GBText variant="bodyMedium" color="secondary" center>
            Its Greenback Business subscription isn't paid up, so its books are read-only and
            the rest of Greenback is locked. Pay for it on its billing page to pick up where you
            left off. Greenback Pro comes with it.
          </GBText>
        </YStack>
      ) : (
      <YStack alignItems="center" gap="$4" paddingVertical="$2">
        <GBBadge variant="brand" size="md">
          <GBBadge.Text variant="brand" size="md">GREENBACK PRO</GBBadge.Text>
        </GBBadge>
        <GBText variant="headlineMedium" bold center color="primary">
          Unlock Greenback
        </GBText>
        <GBText variant="bodyMedium" color="secondary" center>
          Subscribe to Greenback Pro to access your finances — banking, budgets,
          investments, AI insights, and more.
        </GBText>
        <PricingNoteContainer>
          <GBText variant="bodySmall" color="secondary" center>
            Pick the plan that fits — monthly, annual, or other options on the next page.
          </GBText>
        </PricingNoteContainer>
      </YStack>
      )}
    </GBModal>
  );
};

const PricingNoteContainer = styled.div`
  background: ${({ theme }) => theme.background.subtle};
  border-radius: 16px;
  padding: 16px 20px;
  width: 100%;
  text-align: center;
`;
